import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { FIRM_TYPES, computeMetrics } from "@/sections/FirmOperationsSnapshot";

/* ---------------- Report page ----------------
   Print-friendly, personalized snapshot generated from URL params:
     /report?firm=<id>&intakes=<n>&name=<viewer>
   Rendered outside the site chrome via query so it can be printed to PDF.
------------------------------------------------ */

const GEN_TABLE = {
    1: { label: "Gen 1 · Solo grinder",         plateau: "Phone barely answered, no ops leverage",       upgrade: "Gen 3 — production feel of a 10+ attorney firm" },
    2: { label: "Gen 2 · The Family Firm",      plateau: "Manual intake, slipping quality on volume",     upgrade: "Gen 4 — regional-tier throughput without regional headcount" },
    3: { label: "Gen 3 · The Growth Firm",      plateau: "Missed calls, coverage gaps, adjuster leaks",   upgrade: "Gen 4/5 — institution-grade ops with your current team" },
    4: { label: "Gen 4 · Regional Powerhouse",  plateau: "Headcount fixing system problems",              upgrade: "Gen 5 — orchestrated agent layer across every workflow" },
    5: { label: "Gen 5 · The Institution",      plateau: "Every workflow already at scale",               upgrade: "Deep custom agent design across intake, ops, and case mgmt" },
};

const ACTIVITY_ROWS = (m) => [
    { activity: "Reception & call handling",              hours: m.receptionHours, agent: "Intelligent Reception" },
    { activity: "Intake processing & data entry",          hours: m.intakeHours,    agent: "Lead Qualification" },
    { activity: "Follow-ups: records, signatures, adjusters", hours: m.followupHours, agent: "Document Follow-up / Medical Records Retrieval / Adjuster Agent" },
    { activity: "Voicemail & after-hours recovery",        hours: m.voicemailHours, agent: "Voicemail Recovery / After-Hours Concierge" },
];

const fmt = (n, decimals = 0) =>
    Number(n).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const ReportPage = () => {
    const location = useLocation();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const firmId = params.get("firm") || "family";
    const intakes = Math.max(2, Math.min(1500, parseInt(params.get("intakes") || "30", 10)));
    const viewerName = params.get("name") || "";

    const firm = useMemo(
        () => FIRM_TYPES.find((f) => f.id === firmId) || FIRM_TYPES[1],
        [firmId]
    );
    const m = useMemo(() => computeMetrics(intakes), [intakes]);
    const rows = useMemo(() => ACTIVITY_ROWS(m), [m]);
    const gen = GEN_TABLE[firm.gen];
    const upgradedGen = GEN_TABLE[Math.min(5, firm.gen + 1)] || gen;

    return (
        <main
            data-testid="report-page"
            className="min-h-screen bg-[var(--paper)] py-12 md:py-16 print:py-6"
        >
            <div className="max-w-[860px] mx-auto px-6 md:px-10">
                {/* Header */}
                <div className="flex items-start justify-between gap-6 border-b border-[var(--border)] pb-6 mb-8">
                    <div>
                        <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--ink-dim)]">
                            Lexafide · Operations Snapshot
                        </div>
                        <h1 className="font-display text-[clamp(1.9rem,3.6vw,2.8rem)] leading-tight tracking-tight text-[var(--ink)] font-semibold mt-3">
                            Operations Snapshot — <span className="accent-blue">{firm.label}</span>
                        </h1>
                        {viewerName && (
                            <p className="mt-2 text-[13.5px] text-[var(--ink-soft)]">
                                Prepared for {viewerName} · {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-col items-end gap-2 print:hidden">
                        <button
                            type="button"
                            onClick={() => window.print()}
                            data-testid="report-print-btn"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[var(--border-strong)] bg-[var(--paper)] text-[var(--ink)] font-medium text-[13px] hover:bg-[var(--ink)] hover:text-white transition-colors"
                        >
                            <i className="fa-solid fa-print text-xs" />
                            Print / Save as PDF
                        </button>
                        <a
                            href="/"
                            className="text-[11px] font-mono uppercase tracking-[0.15em] text-[var(--ink-dim)] hover:text-[var(--ink)]"
                        >
                            ← back to lexafide.com
                        </a>
                    </div>
                </div>

                {/* Summary numbers */}
                <section className="mb-10" data-testid="report-summary">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <ReportStat label="Signed cases / mo" value={fmt(intakes)} />
                        <ReportStat label="New inquiries / mo" value={fmt(m.newInquiries)} />
                        <ReportStat label="Active matters" value={fmt(m.activeMatters)} />
                        <ReportStat label="Inbound calls / mo" value={fmt(m.inboundCalls)} />
                    </div>
                    <div className="mt-4 rounded-xl border border-[rgba(30,91,255,0.35)] bg-[rgba(30,91,255,0.06)] p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-dim)]">
                                Operational hours / mo
                            </div>
                            <div className="font-display text-3xl font-semibold tabular-nums text-[var(--ink)] mt-1">
                                {fmt(m.opsHours)} hrs
                            </div>
                            <div className="text-[12px] text-[var(--ink-soft)] mt-1">
                                ≈ {fmt(m.fullTimeStaff, 1)} full-time staff
                            </div>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] tracking-[0.18em] uppercase accent-blue">
                                Lexafide absorbs
                            </div>
                            <div className="font-display text-3xl font-semibold tabular-nums text-[var(--ink)] mt-1">
                                {fmt(m.reclaimedHours)} hrs
                            </div>
                            <div className="text-[12px] text-[var(--ink-soft)] mt-1">reclaimed / mo</div>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] tracking-[0.18em] uppercase accent-blue">
                                Equivalent staff cost
                            </div>
                            <div className="font-display text-3xl font-semibold tabular-nums accent-blue mt-1">
                                ${fmt(m.dollarValue)}
                            </div>
                            <div className="text-[12px] text-[var(--ink-soft)] mt-1">/ year</div>
                        </div>
                    </div>
                </section>

                {/* Activity table */}
                <section className="mb-10" data-testid="report-activity-table">
                    <h2 className="font-display text-xl md:text-2xl font-semibold text-[var(--ink)] mb-4">
                        Where the hours go
                    </h2>
                    <div className="rounded-xl border border-[var(--border)] overflow-hidden">
                        <table className="w-full text-left text-[13.5px]">
                            <thead>
                                <tr className="bg-[var(--cream-2)] text-[var(--ink-dim)] font-mono uppercase tracking-[0.14em]">
                                    <th className="px-4 py-3 text-[10.5px] font-semibold">Activity</th>
                                    <th className="px-4 py-3 text-[10.5px] font-semibold text-right">Hours / mo</th>
                                    <th className="px-4 py-3 text-[10.5px] font-semibold">Lexafide agent</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--border)]">
                                {rows.map((r) => (
                                    <tr key={r.activity} className="bg-[var(--paper)]">
                                        <td className="px-4 py-3 text-[var(--ink)] font-medium">{r.activity}</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-[var(--ink)]">{fmt(r.hours)}</td>
                                        <td className="px-4 py-3 text-[var(--ink-soft)]">{r.agent}</td>
                                    </tr>
                                ))}
                                <tr className="bg-[var(--cream-2)]">
                                    <td className="px-4 py-3 font-semibold text-[var(--ink)]">Total operational hours</td>
                                    <td className="px-4 py-3 text-right font-semibold tabular-nums accent-blue">{fmt(m.opsHours)}</td>
                                    <td className="px-4 py-3 text-[var(--ink-soft)]">
                                        {fmt(m.reclaimedHours)} hrs ({Math.round(60)}%) absorbed by Lexafide agents
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Ops maturity */}
                <section className="mb-10" data-testid="report-maturity">
                    <h2 className="font-display text-xl md:text-2xl font-semibold text-[var(--ink)] mb-4">
                        Ops maturity — where a firm your size typically sits
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-[var(--border)] bg-[var(--paper)] p-5">
                            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-dim)] mb-1.5">
                                Today
                            </div>
                            <div className="font-display font-semibold text-[var(--ink)]">{gen.label}</div>
                            <p className="mt-2 text-[13px] text-[var(--ink-soft)] leading-relaxed">{gen.plateau}</p>
                        </div>
                        <div className="rounded-xl border border-[rgba(30,91,255,0.35)] bg-[rgba(30,91,255,0.06)] p-5">
                            <div className="font-mono text-[10px] tracking-[0.18em] uppercase accent-blue mb-1.5">
                                With Lexafide agents deployed
                            </div>
                            <div className="font-display font-semibold text-[var(--ink)]">
                                Operate like {upgradedGen.label.split("·")[1]?.trim() || upgradedGen.label}
                            </div>
                            <p className="mt-2 text-[13px] text-[var(--ink-soft)] leading-relaxed">{upgradedGen.upgrade}</p>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="mb-8 rounded-2xl bg-[var(--ink)] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
                    <div className="flex-1">
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/55 mb-2">
                            Next step
                        </div>
                        <h3 className="font-display text-xl md:text-2xl font-semibold leading-tight">
                            30 minutes with our CEO. Walk through your workflows, get a real map.
                        </h3>
                    </div>
                    <a
                        href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="report-cta-btn"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[var(--ink)] font-medium text-sm hover:bg-[var(--blue)] hover:text-white transition-colors"
                    >
                        <i className="fa-solid fa-calendar-days text-xs" />
                        Book a 30-min demo
                    </a>
                </section>

                {/* Disclaimer */}
                <p className="text-[11.5px] text-[var(--ink-dim)] leading-relaxed">
                    Benchmarks from plaintiff firms in production on Lexafide: ~33% inquiry-to-signed conversion, 14-month average matter lifecycle, 11 min average call handle time, 60% automation rate, $38/hr blended paralegal cost. Estimates — your firm will vary. © {new Date().getFullYear()} Lexafide.
                </p>
            </div>

            <style>{`
                @media print {
                    header, footer, nav { display: none !important; }
                    body { background: white !important; }
                }
            `}</style>
        </main>
    );
};

const ReportStat = ({ label, value }) => (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--paper)] p-4">
        <div className="font-mono text-[9.5px] tracking-[0.16em] uppercase text-[var(--ink-dim)]">{label}</div>
        <div className="font-display text-2xl font-semibold tabular-nums text-[var(--ink)] mt-1">{value}</div>
    </div>
);

export default ReportPage;
